import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { SlidersHorizontal, Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDue, erAktivStage } from "./blokkData";
import AssignmentTabell from "./AssignmentTabell";

const dueDenneUken = (frist) => {
  const d = fmtDue(frist);
  if (!d) return false;
  return d.tone !== "" || (() => {
    const dato = new Date(`${String(frist).slice(0, 10)}T12:00:00`);
    const naa = new Date();
    naa.setHours(12, 0, 0, 0);
    return (dato - naa) / 86400000 <= 6;
  })();
};

function NyRad({ kilde, onLagd }) {
  const [aapen, setAapen] = useState(false);
  const [navn, setNavn] = useState("");
  const [type, setType] = useState(kilde === "partners" ? "partner" : "speaker");
  const [jobber, setJobber] = useState(false);
  const laast = kilde === "speakers" || kilde === "partners";
  const send = async (e) => {
    e.preventDefault();
    if (!navn.trim() || jobber) return;
    setJobber(true);
    try {
      const t = laast ? (kilde === "partners" ? "partner" : "speaker") : type;
      if (t === "partner") await api.post("/admin/os/partners", { name: navn.trim(), actor: "Dokument" });
      else await api.post("/admin/os/speakers", { name: navn.trim(), actor: "Dokument" });
      toast.success(`${navn.trim()} lagt til`);
      setNavn("");
      setAapen(false);
      onLagd();
    } catch {
      toast.error("Kunne ikke legge til");
    } finally {
      setJobber(false);
    }
  };
  if (!aapen) {
    return (
      <button type="button" className="dok-nyknapp" onClick={() => setAapen(true)} data-testid="dok-master-ny">
        <Plus size={13} /> Ny {kilde === "partners" ? "partner" : kilde === "speakers" ? "speaker" : "target"}
      </button>
    );
  }
  return (
    <form className="dok-ny-form" onSubmit={send} data-testid="dok-master-ny-form">
      {!laast && (
        <select value={type} onChange={(e) => setType(e.target.value)} className="dok-blokk-input velg" data-testid="dok-master-ny-type">
          <option value="speaker">Speaker</option>
          <option value="partner">Partner</option>
        </select>
      )}
      <input autoFocus value={navn} onChange={(e) => setNavn(e.target.value)} disabled={jobber}
        placeholder={`Navn på ny ${laast ? (kilde === "partners" ? "partner" : "speaker") : type}`}
        onKeyDown={(e) => e.key === "Escape" && setAapen(false)}
        className="dok-blokk-input" data-testid="dok-master-ny-input" />
      <button type="submit" className="dok-atab-ny-lagre" disabled={jobber || !navn.trim()} data-testid="dok-master-ny-lagre">
        {jobber ? "…" : "Legg til"}
      </button>
      <button type="button" className="dok-nyknapp lukk" onClick={() => setAapen(false)} data-testid="dok-master-ny-lukk"><X size={12} /></button>
    </form>
  );
}

const VISNINGER = [["flat", "Alle i én tabell"], ["owner", "Gruppert på eier"], ["kategori", "Gruppert på kategori"]];

export default function MasterVisning({ rader, naviger, spec, endre, editable, refresh, kontroller }) {
  const [gruppering, setGruppering] = useState(spec?.gruppering || "flat");
  const [eier, setEier] = useState(null);
  const [visFilter, setVisFilter] = useState(false);
  const [frys, setFrys] = useState(() => {
    const v = parseInt(localStorage.getItem("nas_dok_frys") ?? "1", 10);
    return Number.isNaN(v) ? 1 : Math.min(2, Math.max(0, v));
  });
  const settFrys = (n) => {
    setFrys(n);
    localStorage.setItem("nas_dok_frys", String(n));
  };
  const [kompakt, setKompakt] = useState(() => localStorage.getItem("nas_dok_tetthet") === "kompakt");
  const settKompakt = (v) => {
    setKompakt(v);
    localStorage.setItem("nas_dok_tetthet", v ? "kompakt" : "komfort");
  };
  const [slettRad, setSlettRad] = useState(null);
  const [sletter, setSletter] = useState(false);
  const [typeFilter, setTypeFilter] = useState(null);
  const sisteSlett = useRef(null);
  const popRef = useRef(null);

  const gjenopprett = useCallback(async () => {
    const s = sisteSlett.current;
    if (!s) return;
    sisteSlett.current = null;
    try {
      await api.post(`/admin/os/gjenopprett/${s.arkivId}`);
      toast.success(`${s.navn} er gjenopprettet`);
      refresh();
    } catch {
      toast.error("Kunne ikke gjenopprette");
    }
  }, [refresh]);

  useEffect(() => {
    const taster = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "z" || e.shiftKey) return;
      if (!sisteSlett.current) return;
      const t = e.target;
      if (t && (t.closest?.(".ProseMirror") || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName) || t.isContentEditable)) return;
      e.preventDefault();
      e.stopPropagation();
      gjenopprett();
    };
    window.addEventListener("keydown", taster, true);
    return () => window.removeEventListener("keydown", taster, true);
  }, [gjenopprett]);

  useEffect(() => {
    if (!slettRad) return undefined;
    const esc = (e) => { if (e.key === "Escape") setSlettRad(null); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [slettRad]);

  const bekreftSlett = async () => {
    const r = slettRad;
    if (!r || sletter) return;
    setSletter(true);
    try {
      const url = r.type === "partner" ? `/admin/os/partners/${r.id}`
        : r.type === "speaker" ? `/admin/os/speakers/${r.id}`
          : `/admin/os/kontakter/${r.id}`;
      const { data } = await api.delete(url);
      if (data?.arkivId) sisteSlett.current = { arkivId: data.arkivId, navn: r.navn };
      toast.success(`${r.navn} er slettet`, {
        action: { label: "Angre (Ctrl+Z)", onClick: gjenopprett },
        duration: 8000,
      });
      setSlettRad(null);
      refresh();
    } catch {
      toast.error("Kunne ikke slette");
    } finally {
      setSletter(false);
    }
  };

  useEffect(() => {
    if (!visFilter) return undefined;
    const klikk = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setVisFilter(false); };
    const esc = (e) => { if (e.key === "Escape") setVisFilter(false); };
    document.addEventListener("mousedown", klikk);
    window.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", klikk); window.removeEventListener("keydown", esc); };
  }, [visFilter]);

  const settGruppering = (k) => {
    setGruppering(k);
    if (editable) endre?.({ gruppering: k });
  };

  const eiere = useMemo(() => {
    const m = new Map();
    rader.forEach((r) => {
      const id = r.eierId || "uten";
      const e = m.get(id) || { id, navn: r.ansvarlig || "Unassigned", farge: r.eierFarge, antall: 0 };
      e.antall += 1;
      m.set(id, e);
    });
    return [...m.values()].sort((a, b) => (a.id === "uten") - (b.id === "uten") || b.antall - a.antall);
  }, [rader]);

  const synlige = useMemo(() => {
    let liste = eier ? rader.filter((r) => (r.eierId || "uten") === eier) : rader;
    if (typeFilter) liste = liste.filter((r) => (typeFilter === "org" ? r.targetType === "org" : (r.targetType || "person") === "person"));
    return liste;
  }, [rader, eier, typeFilter]);

  const stats = useMemo(() => ({
    prioritet: rader.filter((r) => r.pri === "P1" || r.pri === "P2").length,
    now: rader.filter((r) => (r.fokus || "") === "now").length,
    uken: rader.filter((r) => dueDenneUken(r.frist)).length,
    aktiv: rader.filter((r) => erAktivStage(r.stage)).length,
    uten: rader.filter((r) => !r.eierId).length,
    total: rader.length,
  }), [rader]);

  const leggTilKontakt = async (gruppe, valg) => {
    try {
      await api.post("/admin/os/kontakter", {
        name: valg.navn,
        kategori: valg.kategori || (gruppe.mode === "kategori" ? gruppe.id : "annet"),
        ownerId: gruppe.mode === "owner" && gruppe.id !== "uten" ? gruppe.id : null,
      });
      toast.success("Kontakt lagt til");
      refresh();
    } catch {
      toast.error("Kunne ikke legge til kontakt");
    }
  };

  const eierNavn = eier ? (eiere.find((e) => e.id === eier)?.navn || "") : "";
  const aktivFilter = (eier ? 1 : 0) + (gruppering !== "flat" ? 1 : 0) + (typeFilter ? 1 : 0);

  return (
    <div className="dok-sprint" contentEditable={false} data-testid="dok-master">
      <div className="dok-master-topp">
        <span className="dok-blokk-overskrift">{spec?.tittel || "Master outreach"}</span>
        <span className="dok-filter-wrap" ref={popRef}>
          <button type="button" className={`dok-filterknapp${aktivFilter ? " harfilter" : ""}`}
            onClick={() => setVisFilter((v) => !v)} data-testid="dok-master-filter">
            <SlidersHorizontal size={13} /> Filter{aktivFilter ? <b>{aktivFilter}</b> : null}
          </button>
          {visFilter && (
            <div className="dok-filter-pop" data-testid="dok-filter-pop">
              <p className="t">View</p>
              <div className="valg">
                {VISNINGER.map(([k, l]) => (
                  <button key={k} type="button" onClick={() => settGruppering(k)}
                    className={gruppering === k ? "aktiv" : ""} data-testid={`dok-master-visning-${k}`}>{l}</button>
                ))}
              </div>
              <p className="t">Owner</p>
              <div className="valg">
                <button type="button" onClick={() => setEier(null)} className={!eier ? "aktiv" : ""} data-testid="dok-master-eier-alle">Alle</button>
                {eiere.map((m) => (
                  <button key={m.id} type="button" onClick={() => setEier(eier === m.id ? null : m.id)}
                    className={eier === m.id ? "aktiv" : ""} data-testid={`dok-master-eier-${m.id}`}>
                    {m.id === "uten" ? <i className="ut">!</i> : <i style={{ background: m.farge || "#8B5CF6" }}>{m.navn[0]}</i>}
                    {m.navn} <b>{m.antall}</b>
                  </button>
                ))}
              </div>
              {spec?.kildeMaster === "speakers" && (
                <>
                  <p className="t">Type</p>
                  <div className="valg">
                    <button type="button" onClick={() => setTypeFilter(null)} className={!typeFilter ? "aktiv" : ""} data-testid="dok-master-type-alle">Alle</button>
                    <button type="button" onClick={() => setTypeFilter(typeFilter === "person" ? null : "person")} className={typeFilter === "person" ? "aktiv" : ""} data-testid="dok-master-type-person">People</button>
                    <button type="button" onClick={() => setTypeFilter(typeFilter === "org" ? null : "org")} className={typeFilter === "org" ? "aktiv" : ""} data-testid="dok-master-type-org">Organisations</button>
                  </div>
                </>
              )}
              <p className="t">Frys kolonner</p>
              <div className="valg">
                {[[0, "Ingen"], [1, "Target"], [2, "Target + neste"]].map(([n, l]) => (
                  <button key={n} type="button" onClick={() => settFrys(n)}
                    className={frys === n ? "aktiv" : ""} data-testid={`dok-master-frys-${n}`}>{l}</button>
                ))}
              </div>
              <p className="t">Tetthet</p>
              <div className="valg">
                <button type="button" onClick={() => settKompakt(false)} className={!kompakt ? "aktiv" : ""} data-testid="dok-master-tetthet-komfort">Komfortabel</button>
                <button type="button" onClick={() => settKompakt(true)} className={kompakt ? "aktiv" : ""} data-testid="dok-master-tetthet-kompakt">Kompakt</button>
              </div>
            </div>
          )}
        </span>
        {eier && (
          <button type="button" className="dok-strip-chip aktiv" onClick={() => setEier(null)} data-testid="dok-master-eier-fjern">
            {eierNavn} <X size={10} />
          </button>
        )}
        <span className="flex-1" />
        {editable && <NyRad kilde={spec?.kildeMaster} onLagd={refresh} />}
        {kontroller}
      </div>

      <div className="dok-hero-status" data-testid="dok-master-hero">
        <span className="dok-hero-tall primaer"><b>{stats.now}</b> now</span>
        <span className="dok-hero-tall primaer"><b>{stats.aktiv}</b> active dialogues</span>
        <span className={`dok-hero-tall primaer${stats.uten ? " varsle" : ""}`}><b>{stats.uten}</b> unassigned</span>
        <span className="dok-hero-tall sekundaer">{stats.prioritet} priority targets</span>
        <span className="dok-hero-tall total"><b>{stats.total}</b> total</span>
      </div>

      <AssignmentTabell rader={synlige} naviger={naviger} editable={editable}
        gruppering={gruppering} master kilde={spec?.kildeMaster || ""} frys={frys} refresh={refresh} kompakt={kompakt}
        onNyKontakt={leggTilKontakt} onSlettRad={editable ? setSlettRad : null} />

      {slettRad && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[22vh] px-4" data-testid="dok-slett-modal">
          <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={() => setSlettRad(null)} />
          <div className="sak-modal relative w-full max-w-[400px] bg-white rounded-2xl shadow-2xl border border-[#E4E4E1] p-6">
            <h2 className="text-[15px] font-semibold" data-testid="dok-slett-modal-tittel">Slette {slettRad.navn}?</h2>
            <p className="text-[12.5px] text-neutral-500 mt-2 leading-relaxed">
              {slettRad.type === "partner" ? "Partneren" : slettRad.type === "speaker" ? "Speakeren" : "Kontakten"} fjernes
              fra masterlisten og hele systemet. Dette kan ikke angres.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setSlettRad(null)}
                className="text-[12.5px] font-medium text-neutral-600 border border-[#E4E4E1] rounded-lg px-3.5 py-2 hover:border-neutral-400 transition-colors"
                data-testid="dok-slett-avbryt">Avbryt</button>
              <button type="button" onClick={bekreftSlett} disabled={sletter}
                className="text-[12.5px] font-semibold text-white bg-red-600 rounded-lg px-3.5 py-2 hover:bg-red-700 disabled:opacity-50 transition-colors"
                data-testid="dok-slett-bekreft">{sletter ? "Sletter …" : "Slett"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
